import { logger } from "../logger";

/**
 * Client half of the Meta Conversions API (CAPI) integration.
 *
 * Sends a server-side copy of each funnel event to our own edge function
 * (supabase/functions/meta-capi), which relays it to Meta. Because the request
 * goes to our own domain — not facebook.com/tr — it survives the ad blockers
 * and browser restrictions that silently drop the client pixel. Meta collapses
 * the browser + server copies using a shared event_id (see meta-pixel.ts).
 *
 * A complete no-op unless VITE_META_CAPI_URL is set, so it stays inert until the
 * edge function is deployed and pointed at. Set it to the function URL, e.g.
 *   https://<project-ref>.supabase.co/functions/v1/meta-capi
 */

const CAPI_URL = import.meta.env.VITE_META_CAPI_URL;

/** Read a first-party cookie by name (undefined if absent). */
function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + escaped + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

export function isCapiEnabled(): boolean {
  return Boolean(CAPI_URL);
}

/**
 * Send a server-side copy of a Meta event. Fire-and-forget: never throws, never
 * blocks the UI, and survives page unload via keepalive. `eventId` MUST be the
 * same id used for the browser pixel event so Meta de-duplicates the pair.
 */
export function sendCapiEvent(metaEventName: string, eventId: string): void {
  if (!CAPI_URL) return;
  if (typeof window === "undefined") return;

  const payload = {
    event_name: metaEventName,
    event_id: eventId,
    event_source_url: window.location.href,
    fbp: readCookie("_fbp"),
    fbc: readCookie("_fbc"),
  };

  try {
    void fetch(CAPI_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: "omit",
    }).catch(() => {
      /* tracking must never break the app */
    });
  } catch (error) {
    logger.debug("Meta CAPI send failed", {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}
